import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { HTTPError } from 'ky';
import INodeAgent from 'types/INodeAgent';
import { drainNode, resumeNode } from 'store/nodeDrain';

interface Props {
  node: INodeAgent;
  mode: 'drain' | 'resume';
  onClose: () => void;
  onDone: () => void;
}

export function describeDrain(drain: NonNullable<INodeAgent['drain']>): string {
  const parts = [`${drain.workers_stopped}/${drain.workers_total} workers stopped`];
  if (drain.tasks_pausing) parts.push(`${drain.tasks_pausing} pausing`);
  parts.push(`${drain.tasks_paused} paused`);
  if (drain.tasks_running) parts.push(`${drain.tasks_running} running`);
  if (drain.tasks_finished) parts.push(`${drain.tasks_finished} finished`);
  return parts.join(' · ');
}

async function errorMessage(error: unknown): Promise<string> {
  if (error instanceof HTTPError) {
    try {
      const body = (await error.response.json()) as { detail?: string };
      if (body?.detail) return body.detail;
    } catch {
      /* fall through */
    }
    return `${error.response.status} ${error.response.statusText}`;
  }
  return error instanceof Error ? error.message : String(error);
}

export default function NodeDrainDialog({ node, mode, onClose, onDone }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nodeName = node.name || node.node_id;
  const drain = node.drain ?? null;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (mode === 'drain') await drainNode(node.id);
      else await resumeNode(node.id);
      onDone();
      onClose();
    } catch (e) {
      setError(await errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === 'drain' ? `Drain ${nodeName}` : `Resume ${nodeName}`}</DialogTitle>
      <DialogContent>
        {mode === 'drain' ? (
          <>
            <DialogContentText>
              Retires every managed worker on this node and pauses their pausable running tasks. Workers stop taking new
              work; each pausable task checkpoints and frees its slot; tasks that cannot pause keep running until they
              finish. No worker can be started on the node until it is resumed.
            </DialogContentText>
            <DialogContentText sx={{ mt: 1 }}>
              Reboot once every retired worker has stopped — tasks still running at that point are killed and surface as
              worker lost.
            </DialogContentText>
            {drain && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Already draining: {describeDrain(drain)}. Draining again pauses tasks that started since.
              </Typography>
            )}
          </>
        ) : (
          <>
            <DialogContentText>
              Re-queues the {drain?.tasks_paused ?? 0} paused task{drain?.tasks_paused === 1 ? '' : 's'} — they resume
              on this node before it takes other work — then restarts the {drain?.workers_total ?? 0} retired worker
              {drain?.workers_total === 1 ? '' : 's'}.
            </DialogContentText>
            {drain && drain.tasks_pausing > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {drain.tasks_pausing} pause{drain.tasks_pausing === 1 ? ' is' : 's are'} still in flight; those tasks
                re-queue as soon as their checkpoint is confirmed.
              </Alert>
            )}
            {drain && drain.tasks_running > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {drain.tasks_running} task{drain.tasks_running === 1 ? ' is' : 's are'} still running on the node.
              </Alert>
            )}
          </>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={submit} variant="contained" color={mode === 'drain' ? 'warning' : 'primary'} disabled={busy}>
          {busy ? (mode === 'drain' ? 'Draining…' : 'Resuming…') : mode === 'drain' ? 'Drain' : 'Resume'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
